import { MEETING_VISIBILITIES, ONLINE_PLATFORMS, QUOTE_FLOW_STATUSES } from './types'
import type {
  Channel,
  Collection,
  CollectionStatus,
  CrmFile,
  CrmOverview,
  Customer,
  CustomerSummary,
  Delivery,
  EmailLog,
  FileCategory,
  Followup,
  Invoice,
  LicenseTerm,
  Meeting,
  MeetingResult,
  MeetingVisibility,
  QuoteFlowStatus,
  MinuteItem,
  ModuleStatus,
  OnlinePlatform,
  Offer,
  OfferItem,
  OfferStatus,
  OfferSummary,
  Order,
  OrderStatus,
  Priority,
  Receipt,
  Renewal,
  RenewalKind,
  RenewalStatus,
  RequestStatus,
  Stage,
  SupportRequest,
  TeamRole,
  TimelineItem,
  Trial,
  TrialStatus,
  WorkKind,
  WorkRecord,
  WorkStatus,
} from './types'
import { isoDay, toDate } from '../shared/format'
import { CHANNELS, DELIVERY, FILE_CATEGORIES, LICENSE_TERMS, MODULE_STATUSES, OFFER_STATUSES, ORDER_STATUSES, PRIORITIES, RENEWAL_KINDS, RENEWAL_STATUSES, REQUEST_STATUSES, RESULTS, STAGES, TEAM_ROLES, TRIAL_STATUSES, WORK_KINDS, WORK_STATUSES } from './types'

export type Row = Record<string, unknown>

export type StageRow = { code: string; kind: string; is_active?: boolean }

export type Names = {
  sectorName: Map<string, string>
  regionName: Map<string, string>
  regionId: Map<string, string>
  sales: StageRow[]
  install: StageRow[]
}

export const emptyNames = (): Names => ({
  sectorName: new Map(),
  regionName: new Map(),
  regionId: new Map(),
  sales: [],
  install: [],
})

export function asRow(v: unknown): Row {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Row) : {}
}

export function str(v: unknown, fallback = ''): string {
  if (typeof v === 'string') return v
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  if (typeof v === 'boolean') return v ? 'true' : 'false'
  return fallback
}

export function num(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim()) {
    const n = Number(v.replace(',', '.'))
    if (Number.isFinite(n)) return n
  }
  return 0
}

export function money(v: unknown): string {
  if (typeof v === 'number' && Number.isFinite(v)) return v.toFixed(2)
  if (typeof v === 'string' && v.trim()) {
    const n = Number(v.replace(/\s/g, '').replace(',', '.'))
    if (Number.isFinite(n)) return n.toFixed(2)
  }
  return '0.00'
}

/** API tarih/zaman alanını yerel `YYYY-MM-DD` gününe çevirir (TR damga, ISO ya da gün). */
export function datePart(v: unknown): string {
  return isoDay(str(v))
}

/** API zaman damgasından yerel `HH:mm`. */
export function timePart(v: unknown): string {
  const d = toDate(str(v))
  return d ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : ''
}

/** Sıralama için zaman damgası (ms); geçersizse 0. */
export function stamp(v: unknown): number {
  return toDate(str(v))?.getTime() ?? 0
}

export function isoAt(date: string, time = '09:00'): string {
  if (!date) return ''
  const clock = time.length >= 4 ? time.slice(0, 5) : '09:00'
  const d = new Date(`${date}T${clock}:00`)
  return Number.isNaN(d.getTime()) ? `${date}T${clock}:00Z` : d.toISOString()
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

export function daysBetween(a: string, b: string): number {
  const left = Date.parse(`${a.slice(0, 10)}T00:00:00Z`)
  const right = Date.parse(`${b.slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(left) || Number.isNaN(right)) return 0
  return Math.round((left - right) / 86_400_000)
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  const s = str(value)
  return (allowed as readonly string[]).includes(s) ? (s as T) : fallback
}

export function pack(...parts: string[]): string {
  return parts.filter(Boolean).join('~')
}

export function unpack(id: string): string[] {
  return id.split('~')
}

export function stageOf(value: unknown, stages: StageRow[] = []): Stage {
  const code = str(value).trim()
  const hit = stages.find((s) => s.code === code)
  const s = code.toLowerCase()
  if (hit?.kind === 'won') return 'customer'
  if (hit?.kind === 'lost') return 'lost'
  if (hit?.kind === 'open') {
    if (s.includes('demo')) return 'demo'
    if (s.includes('teklif') || s.includes('proposal') || s.includes('muzak')) return 'proposal'
    return 'lead'
  }
  if ((STAGES as readonly string[]).includes(s)) return s as Stage
  if (s.includes('kay') || s.includes('lost')) return 'lost'
  if (s.includes('kazan') || s.includes('muster') || s.includes('müşter') || s.includes('won')) return 'customer'
  if (s.includes('teklif') || s.includes('muzak') || s.includes('proposal')) return 'proposal'
  if (s.includes('demo')) return 'demo'
  return 'lead'
}

export function salesCode(stages: StageRow[], stage: Stage): string {
  const active = stages.filter((s) => s.is_active !== false)
  const byKind = (kind: string) => active.find((s) => s.kind === kind)?.code
  if (stage === 'lost') return byKind('lost') ?? 'kayip'
  if (stage === 'customer') return byKind('won') ?? 'kazanildi'
  if (stage === 'demo') return active.find((s) => s.code.includes('demo'))?.code ?? 'demo'
  if (stage === 'proposal') return active.find((s) => /teklif|proposal|muzak/.test(s.code))?.code ?? 'teklif'
  return byKind('open') ?? active[0]?.code ?? 'yeni'
}

export function deliveryOf(value: unknown, stages: StageRow[] = []): Delivery {
  const code = str(value).trim()
  const hit = stages.find((s) => s.code === code)
  if (hit?.kind === 'done') return 'live'
  const s = code.toLowerCase()
  if ((DELIVERY as readonly string[]).includes(s)) return s as Delivery
  if (!s || s === 'yok') return 'none'
  if (s.includes('canli') || s.includes('canlı') || s.includes('live')) return 'live'
  if (s.includes('kurulum') || s.includes('install') || s.includes('egitim') || s.includes('eğitim') || s.includes('veri')) return 'installing'
  if (s.includes('plan')) return 'planned'
  return 'none'
}

export function installCode(stages: StageRow[], delivery: Delivery): string {
  const active = stages.filter((s) => s.is_active !== false)
  if (delivery === 'live') return active.find((s) => s.kind === 'done')?.code ?? 'canli'
  if (delivery === 'installing') return active.find((s) => /kurulum|install|egitim|veri/.test(s.code))?.code ?? 'kurulum'
  return active.find((s) => s.kind === 'open')?.code ?? 'planlama'
}

export function channelOf(value: unknown): Channel {
  const s = str(value).toLowerCase()
  if ((CHANNELS as readonly string[]).includes(s)) return s as Channel
  if (s.includes('ziyaret') || s.includes('visit') || s.includes('fiziki')) return 'visit'
  if (s.includes('telefon') || s.includes('phone')) return 'phone'
  if (s.includes('posta') || s.includes('mail')) return 'email'
  return 'online'
}

export function followupChannel(channel: Channel): string {
  return { visit: 'ziyaret', phone: 'telefon', online: 'mesaj', email: 'eposta' }[channel]
}

export function meetingKind(channel: Channel): string {
  return { visit: 'fiziki_ziyaret', phone: 'telefon', online: 'online', email: 'eposta' }[channel]
}

export function resultOf(value: unknown): MeetingResult {
  const s = str(value).toLowerCase()
  if ((RESULTS as readonly string[]).includes(s)) return s as MeetingResult
  if (s.includes('ulasil') || s.includes('ulaşıl') || s.includes('not_reached') || s.includes('miss')) return 'not_reached'
  if (s.includes('tekrar') || s.includes('plan') || s.includes('call_again')) return 'call_again'
  return 'reached'
}

export function offerStatusOf(value: unknown): OfferStatus {
  const s = str(value).toLowerCase()
  if (s === 'final' || s === 'sent' || s === 'negotiation' || s === 'gonderildi' || s === 'gönderildi') return 'sent'
  if (s === 'accepted' || s === 'kabul' || s.includes('kabul')) return 'accepted'
  if (s === 'rejected' || s === 'reddedildi' || s === 'red') return 'rejected'
  return oneOf(s, OFFER_STATUSES, 'draft')
}

export function orderStatusOf(value: unknown): OrderStatus {
  const s = str(value).toLowerCase()
  if (s === 'onaylandi' || s === 'onaylandı' || s === 'approved') return 'approved'
  if (s === 'reddedildi' || s === 'rejected' || s === 'iptal') return 'rejected'
  return oneOf(s, ORDER_STATUSES, 'pending')
}

export function teamRoleOf(value: unknown): TeamRole {
  const s = str(value).toLowerCase()
  if ((TEAM_ROLES as readonly string[]).includes(s)) return s as TeamRole
  if (s.includes('owner') || s.includes('sahip') || s.includes('sorumlu')) return 'owner'
  if (s.includes('kurulum') || s.includes('install')) return 'installation'
  return 'support'
}

export function fileCategoryOf(value: unknown): FileCategory {
  const s = str(value).toLowerCase()
  if ((FILE_CATEGORIES as readonly string[]).includes(s)) return s as FileCategory
  if (s.includes('soz') || s.includes('söz') || s.includes('contract')) return 'contract'
  if (s.includes('fatura') || s.includes('invoice')) return 'invoice'
  if (s.includes('dekont') || s.includes('receipt') || s.includes('makbuz')) return 'receipt'
  return 'other'
}

function priorityOf(value: unknown): Priority {
  return oneOf(str(value).toLowerCase(), PRIORITIES, 'medium')
}

export function requestStatusOf(value: unknown): RequestStatus {
  const s = str(value).toLowerCase()
  if ((REQUEST_STATUSES as readonly string[]).includes(s)) return s as RequestStatus
  if (s.includes('coz') || s.includes('çöz') || s.includes('resolv') || s.includes('kapali') || s.includes('kapalı') || s.includes('closed')) return 'resolved'
  if (s.includes('progress') || s.includes('devam') || s.includes('islem') || s.includes('işlem')) return 'in_progress'
  return 'open'
}

export function requestStatusToApi(status: RequestStatus): string {
  return { open: 'acik', in_progress: 'devam', resolved: 'cozuldu' }[status]
}

export function renewalKindOf(value: unknown): RenewalKind {
  const s = str(value).toLowerCase()
  if ((RENEWAL_KINDS as readonly string[]).includes(s)) return s as RenewalKind
  if (s.includes('bakim') || s.includes('bakım') || s.includes('maint')) return 'maintenance'
  if (s.includes('destek') || s.includes('support')) return 'support'
  return 'license'
}

/** FE yenileme türü → BE kodu (abonelik | bakim | destek). */
export function renewalKindToApi(kind: RenewalKind): string {
  return { license: 'abonelik', maintenance: 'bakim', support: 'destek' }[kind]
}

/** FE yenileme durumu → BE kodu (bekliyor | arandi | odendi | iptal). */
export function renewalStatusToApi(status: RenewalStatus): string {
  return { upcoming: 'bekliyor', contacted: 'arandi', renewed: 'odendi', cancelled: 'iptal' }[status]
}

export function renewalStatusOf(value: unknown): RenewalStatus {
  const s = str(value).toLowerCase()
  if ((RENEWAL_STATUSES as readonly string[]).includes(s)) return s as RenewalStatus
  if (s.includes('contact') || s.includes('aran')) return 'contacted'
  if (s.includes('renew') || s.includes('paid') || s.includes('odendi') || s.includes('ödendi')) return 'renewed'
  if (s.includes('cancel') || s.includes('iptal')) return 'cancelled'
  return 'upcoming'
}

export function trialStatusOf(value: unknown, daysLeft: number, orderId: string): TrialStatus {
  if (orderId) return 'converted'
  const s = str(value).toLowerCase()
  if ((TRIAL_STATUSES as readonly string[]).includes(s)) return s === 'active' && daysLeft < 0 ? 'expired' : (s as TrialStatus)
  if (s.includes('cancel') || s.includes('iptal')) return 'cancelled'
  if (s.includes('convert') || s.includes('donus') || s.includes('dönüş')) return 'converted'
  if (s.includes('bitti') || s.includes('expir') || daysLeft < 0) return 'expired'
  return 'active'
}

export function licenseTermOf(value: unknown): LicenseTerm {
  const s = str(value).toLowerCase()
  if ((LICENSE_TERMS as readonly string[]).includes(s)) return s as LicenseTerm
  if (s.includes('month') || s.includes('aylik') || s.includes('aylık')) return 'monthly'
  if (s.includes('year') || s.includes('yill') || s.includes('yıll')) return 'yearly'
  return 'perpetual'
}

function moduleStatusOf(value: unknown, active: unknown): ModuleStatus {
  const s = str(value).toLowerCase()
  if ((MODULE_STATUSES as readonly string[]).includes(s)) return s as ModuleStatus
  if (s.includes('trial') || s.includes('deneme')) return 'trial'
  if (s.includes('plan')) return 'planned'
  if (s.includes('cancel') || s.includes('iptal') || active === false) return 'cancelled'
  return 'active'
}

function workKindOf(value: unknown): WorkKind {
  const s = str(value).toLowerCase()
  if ((WORK_KINDS as readonly string[]).includes(s)) return s as WorkKind
  if (s.includes('egit') || s.includes('eğit') || s.includes('train')) return 'training'
  if (s.includes('demo')) return 'demo'
  return 'installation'
}

function workStatusOf(value: unknown): WorkStatus {
  const s = str(value).toLowerCase()
  if ((WORK_STATUSES as readonly string[]).includes(s)) return s as WorkStatus
  if (s.includes('done') || s.includes('tamam') || s.includes('complete')) return 'done'
  if (s.includes('cancel') || s.includes('iptal')) return 'cancelled'
  if (s.includes('progress') || s.includes('devam') || s.includes('run')) return 'in_progress'
  return 'planned'
}

function collectionStatusOf(row: Row): CollectionStatus {
  const s = str(row.status).toLowerCase()
  if (s === 'paid' || s.includes('oden') || s.includes('öden')) return 'paid'
  const amount = num(row.amount)
  const paid = num(row.paid_amount)
  if (paid > 0 && (amount <= 0 || paid + 0.001 < amount)) return 'partial'
  const due = datePart(row.due_date)
  if (due && due < todayIso() && s !== 'paid') return 'overdue'
  if (s.includes('over') || s.includes('gecik') || s.includes('gecik')) return 'overdue'
  if (s.includes('partial') || s.includes('kismi') || s.includes('kısmi')) return 'partial'
  return 'pending'
}

function urlsOf(links: Row[]) {
  const pick = (...types: string[]) => links.find((l) => types.includes(str(l.link_type).toLowerCase()))
  return {
    website: str(pick('website', 'web', 'site')?.url),
    appUrl: str(pick('app', 'application', 'app_url')?.url),
    adminUrl: str(pick('admin', 'panel', 'admin_url')?.url),
  }
}

export function mapCustomer(account: Row, names: Names, extra?: { contact?: Row | null; links?: Row[]; team?: Row[]; followup?: Row | null; lastMeeting?: string | null; balance?: unknown }): Customer {
  // Liste satırı özetleri kendi içinde taşır (birincil kişi, sıradaki takip, son toplantı, bakiye).
  extra = {
    contact: extra?.contact ?? (account.primary_contact ? asRow(account.primary_contact) : null),
    followup: extra?.followup ?? (account.next_followup ? asRow(account.next_followup) : null),
    lastMeeting: extra?.lastMeeting ?? (str(account.last_meeting_on) || null),
    balance: extra?.balance ?? account.balance,
    links: extra?.links,
    team: extra?.team,
  }
  const contact = extra?.contact ?? null
  const links = urlsOf(extra?.links ?? [])
  const ownerId = str(account.owner_user_id)
  const team = (extra?.team ?? []).map((m) => ({ userId: str(m.user_id), role: teamRoleOf(m.role_label) }))
  const follow = extra?.followup
  const due = follow ? str(follow.due_on) || datePart(follow.due_at) : ''
  return {
    id: str(account.id),
    name: str(account.title),
    legalName: str(account.legal_name),
    sector: names.sectorName.get(str(account.sector_id)) ?? '',
    sectorId: str(account.sector_id),
    projectId: str(account.product_id) || str(account.project_id),
    stage: stageOf(account.sales_stage, names.sales),
    delivery: deliveryOf(account.install_stage, names.install),
    contactName: str(contact?.full_name),
    contactTitle: str(contact?.title),
    phone: str(contact?.phone),
    email: str(contact?.email) || str(account.work_email),
    city: str(account.city),
    district: str(account.district),
    region: names.regionName.get(str(account.region_id)) ?? '',
    taxNo: str(account.tax_id),
    taxOffice: str(account.tax_office),
    website: links.website,
    appUrl: links.appUrl,
    adminUrl: links.adminUrl,
    address: str(account.address),
    ownerId,
    categoryId: str(account.category_id),
    team: team.length ? team : ownerId ? [{ userId: ownerId, role: 'owner' }] : [],
    createdAt: str(account.created_at),
    lastMeeting: extra?.lastMeeting ?? null,
    nextAction: follow && (due || str(follow.note)) ? { date: due, text: str(follow.note), overdue: Boolean(follow.overdue) || (!!due && due < todayIso()) } : null,
    balance: money(extra?.balance),
  }
}

export function mapSummary(finance: Row | null, openRequests: number, acceptedTotal?: string): CustomerSummary {
  const collected = money(finance?.collected_total)
  const pending = money(finance?.pending_collection_total)
  const invoiced = money(finance?.invoiced_total)
  const contract = acceptedTotal && acceptedTotal !== '0.00' ? acceptedTotal : invoiced
  const contractC = Math.round(num(contract) * 100)
  const collectedC = Math.round(num(collected) * 100)
  const remainingC = finance ? Math.round(num(pending) * 100) : Math.max(0, contractC - collectedC)
  return {
    contract,
    collected,
    remaining: (remainingC / 100).toFixed(2),
    collectedPct: contractC === 0 ? 0 : Math.round((collectedC * 100) / contractC),
    openRequests,
  }
}

export function mapTimeline(row: Row): TimelineItem | null {
  const kindRaw = str(row.kind)
  const kind =
    kindRaw === 'meeting'
      ? 'meeting'
      : kindRaw === 'quote'
        ? 'offer'
        : kindRaw === 'ticket'
          ? 'request'
          : kindRaw === 'email'
            ? 'email'
            : kindRaw === 'receipt'
              ? 'receipt'
              : 'work'
  if (!str(row.id) && !str(row.title)) return null
  return {
    id: str(row.id) || `${kind}-${str(row.at)}`,
    at: str(row.at),
    kind,
    title: str(row.title),
    by: str(row.actor_user_id),
    detail: str(row.status),
    badge: str(row.status) || undefined,
    refId: str(row.id),
  }
}

function mapLines(value: unknown): OfferItem[] {
  const list = Array.isArray(value) ? value : []
  return list.map((line, i) => {
    const row = asRow(line)
    return {
      id: str(row.id) || `line-${i}`,
      name: str(row.line_type) === 'extra' ? str(row.description) : str(row.ref_code) || str(row.name) || str(row.line_type),
      qty: Math.max(1, Math.round(num(row.qty) || 1)),
      unitPrice: money(row.unit_price ?? row.unitPrice),
      lineType: str(row.line_type) || 'deliverable',
      refCode: str(row.ref_code),
      description: str(row.description),
      discount: money(row.discount ?? 0),
    }
  })
}

function lineTotals(items: OfferItem[], raw: unknown): Record<string, string> {
  const rows = Array.isArray(raw) ? raw.map(asRow) : []
  const out: Record<string, string> = {}
  items.forEach((item, i) => {
    const src = rows[i]
    const given = src ? str(src.line_total) : ''
    out[item.id] = given ? money(given) : (num(item.unitPrice) * item.qty).toFixed(2)
  })
  return out
}

export function mapOffer(row: Row, orderId: string | null): Offer {
  const items = mapLines(row.lines)
  const status = offerStatusOf(row.status)
  const validUntil = str(row.valid_until)
  const subtotal = str(row.subtotal) ? money(row.subtotal) : items.reduce((s, i) => s + num(i.unitPrice) * i.qty, 0).toFixed(2)
  const discount = str(row.discount_amount) ? money(row.discount_amount) : '0.00'
  const total = str(row.total) ? money(row.total) : Math.max(0, num(subtotal) - num(discount)).toFixed(2)
  return {
    id: str(row.id),
    customerId: str(row.account_id),
    number: str(row.number) || str(row.id).slice(0, 8),
    date: str(row.quote_date) || datePart(row.created_at),
    validUntil,
    status,
    items,
    discountPct: num(row.discount_percent),
    note: str(row.notes),
    subtotal,
    discount,
    total,
    lineTotals: lineTotals(items, row.lines),
    expired: (status === 'draft' || status === 'sent') && !!validUntil && validUntil < todayIso(),
    orderId: orderId ?? (str(row.order_id) || null),
    flowStatus: (QUOTE_FLOW_STATUSES as readonly string[]).includes(str(row.status)) ? (str(row.status) as QuoteFlowStatus) : 'taslak',
    revision: num(row.revision) || 1,
    currentApproved: row.current_approved === true,
    isFinal: row.is_final === true,
    projectId: str(row.project_id),
    showLinePrices: row.show_line_prices === true,
    discountTier: str(row.discount_tier) === 'owner' ? 'owner' : str(row.discount_tier) === 'manager' ? 'manager' : 'staff',
    effectiveDiscount: str(row.effective_discount) || '0',
  }
}

export function offerSummaryOf(list: Offer[]): OfferSummary {
  const open = list.filter((o) => o.status === 'draft' || o.status === 'sent')
  const accepted = list.filter((o) => o.status === 'accepted')
  const rejected = list.filter((o) => o.status === 'rejected')
  const decided = accepted.length + rejected.length
  const sum = (rows: Offer[]) => rows.reduce((s, o) => s + Math.round(num(o.total) * 100), 0)
  return {
    acceptedTotal: (sum(accepted) / 100).toFixed(2),
    openTotal: (sum(open) / 100).toFixed(2),
    openCount: open.length,
    expiredCount: open.filter((o) => o.expired).length,
    acceptanceRate: decided ? Math.round((accepted.length * 100) / decided) : null,
  }
}

export function mapOrder(row: Row): Order {
  const items = mapLines(row.lines)
  const totals = lineTotals(items, row.lines)
  const subtotal = str(row.subtotal) ? money(row.subtotal) : Object.values(totals).reduce((s, v) => s + num(v), 0).toFixed(2)
  const discount = str(row.discount) ? money(row.discount) : '0.00'
  const total = str(row.total) ? money(row.total) : Math.max(0, num(subtotal) - num(discount)).toFixed(2)
  const term = str(row.license_term)
  const license = term
    ? {
        branchLimit: Math.round(num(row.branch_limit)) || 1,
        term: licenseTermOf(term),
        validUntil: datePart(row.valid_until),
        maintenanceUntil: datePart(row.maintenance_until),
        supportUntil: datePart(row.support_until),
      }
    : null
  const status = orderStatusOf(row.status)
  const decided = status !== 'pending'
  return {
    id: str(row.id),
    customerId: str(row.account_id),
    offerId: str(row.quote_id),
    offerNumber: str(row.quote_number) || str(row.quote_id).slice(0, 8),
    number: str(row.number) || str(row.id).slice(0, 8),
    createdAt: str(row.created_at),
    createdBy: str(row.created_by),
    status,
    items,
    lineTotals: totals,
    subtotal,
    discount,
    total,
    license,
    decision: decided ? { by: str(row.approved_by), at: str(row.approved_at) || str(row.created_at), note: str(row.reject_note) } : null,
  }
}

export function mapMeeting(row: Row, expenses: Row[] = [], actions: Row[] = []): Meeting {
  const planned = str(row.planned_at)
  const acts = Array.isArray(row.actions) ? row.actions.map(asRow) : actions
  const exps = Array.isArray(row.expenses) ? row.expenses.map(asRow) : expenses
  const minutes: MinuteItem[] = acts.map((a) => ({
    id: str(a.id),
    text: str(a.title),
    priority: priorityOf(a.priority),
    done: Boolean(a.is_done),
    createdBy: str(a.created_by) || str(a.owner_user_id),
    createdAt: str(a.created_at),
  }))
  // Sonraki adım: vadesi olan ilk açık madde (toplantı formunda girilen takip).
  const next = acts.find((a) => !a.is_done && (str(a.due_on) || str(a.due_at))) ?? null
  const noteBody = str(row.minutes)
  const staff = Array.isArray(row.staff_user_ids) ? row.staff_user_ids.map((x) => str(x)).filter(Boolean) : []
  return {
    id: str(row.id),
    customerId: str(row.account_id),
    date: str(row.planned_on) || datePart(planned),
    start: timePart(planned),
    end: timePart(row.ends_at),
    staffId: staff[0] ?? str(row.owner_user_id),
    ownerId: str(row.owner_user_id),
    staffIds: staff,
    contactIds: Array.isArray(row.contact_ids) ? row.contact_ids.map((x) => str(x)).filter(Boolean) : [],
    customerResponse: str(row.customer_response),
    decisions: str(row.decisions),
    channel: channelOf(row.kind),
    result: resultOf(row.status),
    // Gizli toplantıda konu da gelmez (sunucu maskeler).
    summary: str(row.subject) || noteBody || str(row.prep_notes) || (row.restricted === true ? 'Gizli toplantı' : ''),
    nextAction: next ? { date: str(next.due_on) || datePart(next.due_at), staffId: str(next.owner_user_id), text: str(next.title) } : null,
    expenses: exps.map((e) => ({
      id: str(e.id),
      title: str(e.title),
      amount: money(e.amount),
      date: str(e.spent_on) || datePart(e.created_at),
      fileName: str(e.file_name),
    })),
    notes: noteBody ? [{ id: `${str(row.id)}-note`, author: str(row.owner_user_id), at: planned, body: noteBody }] : [],
    history: [],
    minutes,
    agenda: (Array.isArray(row.agenda) ? row.agenda.map(asRow) : []).map((a) => ({ title: str(a.title), notes: str(a.notes), decision: str(a.decision) })),
    guests: (Array.isArray(row.guests) ? row.guests.map(asRow) : []).map((g) => ({ name: str(g.name), email: str(g.email), company: str(g.company), role: str(g.role) })),
    online: {
      platform: (ONLINE_PLATFORMS as readonly string[]).includes(str(row.online_platform)) ? (str(row.online_platform) as OnlinePlatform) : '',
      url: str(row.online_url),
      code: str(row.online_code),
      hasPasscode: row.online_has_passcode === true,
    },
    visibility: (MEETING_VISIBILITIES as readonly string[]).includes(str(row.visibility)) ? (str(row.visibility) as MeetingVisibility) : 'account',
    restricted: row.restricted === true,
  }
}

export function mapFollowup(row: Row): Followup {
  const due = datePart(row.due_at)
  const doneAt = str(row.done_at)
  const done = Boolean(doneAt)
  return {
    id: str(row.id),
    customerId: str(row.account_id),
    channel: channelOf(row.channel),
    dueOn: due,
    ownerId: str(row.owner_user_id),
    note: str(row.note),
    done,
    doneAt: doneAt || null,
    createdAt: str(row.created_at),
    overdue: done ? false : Boolean(row.overdue) || (!!due && due < todayIso()),
  }
}

export function mapCollection(row: Row): Collection {
  const accountId = str(row.account_id)
  const amount = money(row.amount)
  const paid = money(row.paid_amount)
  const remaining = Math.max(0, num(amount) - num(paid)).toFixed(2)
  return {
    id: pack(accountId, str(row.id)),
    customerId: accountId,
    code: str(row.title) || str(row.id),
    dueDate: str(row.due_date),
    amount,
    description: str(row.notes),
    paid,
    remaining,
    status: collectionStatusOf(row),
  }
}

export function mapInvoice(row: Row): Invoice {
  const accountId = str(row.account_id)
  const plan = str(row.payment_plan_id)
  return {
    id: pack(accountId, str(row.id)),
    customerId: accountId,
    number: str(row.invoice_no) || str(row.id).slice(0, 8),
    date: str(row.invoice_date) || datePart(row.created_at),
    amount: money(row.amount),
    fileName: str(row.file_name),
    collectionId: plan ? pack(accountId, plan) : null,
    uploadedBy: str(row.created_by),
    uploadedAt: str(row.created_at),
  }
}

export function mapReceipt(row: Row): Receipt {
  const accountId = str(row.account_id)
  const plan = str(row.payment_plan_id)
  return {
    id: pack(accountId, str(row.id)),
    customerId: accountId,
    collectionId: plan ? pack(accountId, plan) : '',
    date: str(row.paid_on) || datePart(row.created_at),
    amount: money(row.amount),
    reference: str(row.method) || str(row.notes),
    fileName: str(row.file_name),
  }
}

export function mapFile(row: Row): CrmFile {
  const accountId = str(row.account_id)
  return {
    id: pack(accountId, str(row.id)),
    customerId: accountId,
    category: fileCategoryOf(row.category),
    title: str(row.title) || str(row.file_name),
    description: str(row.description),
    fileName: str(row.file_name),
    size: Math.round(num(row.size_bytes)),
    visibility: str(row.visibility) === 'all' ? 'all' : 'team',
    meetingId: str(row.meeting_id) || null,
    uploadedAt: str(row.created_at),
    uploadedBy: str(row.uploaded_by),
    linked: row.linked !== false,
    unlinkReason: str(row.unlink_reason),
  }
}

export function mapEmail(row: Row): EmailLog {
  const accountId = str(row.account_id)
  return {
    id: pack(accountId, str(row.id)),
    customerId: accountId,
    to: str(row.to),
    subject: str(row.subject),
    body: str(row.body),
    sentAt: str(row.created_at),
    by: str(row.user_id) || str(row.from),
  }
}

export function mapWork(row: Row): WorkRecord {
  const accountId = str(row.account_id)
  return {
    id: pack(accountId, str(row.id)),
    customerId: accountId,
    kind: workKindOf(row.run_type ?? row.kind),
    date: str(row.scheduled_on) || datePart(row.created_at),
    staffId: str(row.assignee_user_id),
    status: workStatusOf(row.status),
    note: str(row.notes) || str(row.title),
  }
}

export function mapRequest(row: Row, messages: Row[] = []): SupportRequest {
  return {
    id: str(row.id),
    customerId: str(row.account_id),
    number: str(row.number) || `#${str(row.id).slice(0, 8)}`,
    title: str(row.title),
    description: str(row.description),
    module: str(row.module_code),
    priority: priorityOf(row.priority),
    status: requestStatusOf(row.status),
    createdAt: str(row.created_at),
    assigneeId: str(row.assignee_user_id) || null,
    messages: messages.map((m) => ({
      id: str(m.id),
      author: str(m.author_user_id),
      body: str(m.body),
      at: str(m.created_at),
    })),
  }
}

export function mapRenewal(row: Row): Renewal {
  const due = str(row.due_at) || str(row.due_on)
  return {
    id: str(row.id),
    customerId: str(row.account_id),
    orderId: str(row.order_id) || null,
    kind: renewalKindOf(row.kind),
    dueOn: due,
    amount: money(row.amount),
    status: renewalStatusOf(row.status),
    notes: str(row.notes),
    daysLeft: due ? daysBetween(due, todayIso()) : 0,
  }
}

export function mapTrial(row: Row): Trial {
  const ends = str(row.ends_on)
  const daysLeft = Number.isFinite(num(row.days_left)) && str(row.days_left) !== '' ? Math.round(num(row.days_left)) : ends ? daysBetween(ends, todayIso()) : 0
  const orderId = str(row.converted_order_id) || str(row.order_id)
  const extended = Math.round(num(row.extended_days))
  const modules = Array.isArray(row.modules) ? row.modules.map((m) => str(m)).filter(Boolean) : []
  return {
    id: str(row.id),
    customerId: str(row.account_id),
    modules,
    startedOn: str(row.starts_on),
    endsOn: ends,
    status: trialStatusOf(row.status, daysLeft, orderId),
    notes: str(row.notes),
    extensions: extended > 0 ? [{ id: `${str(row.id)}-ext`, days: extended, reason: '', by: '', at: '' }] : [],
    orderId: orderId || null,
    createdBy: str(row.owner_user_id),
    daysLeft,
  }
}

export function mapModule(accountId: string, entitlementId: string, row: Row, term: unknown): { id: string; customerId: string; moduleId: string; name: string; licenseType: 'one_time' | 'monthly' | 'yearly'; startDate: string; status: ModuleStatus; price: string } {
  const code = str(row.code)
  const kind = str(row.kind) || 'module'
  const license = licenseTermOf(term)
  return {
    id: pack(entitlementId, kind, code),
    customerId: accountId,
    moduleId: code,
    name: str(row.name) || code,
    licenseType: license === 'monthly' ? 'monthly' : license === 'yearly' ? 'yearly' : 'one_time',
    startDate: datePart(row.valid_from) || datePart(row.valid_until),
    status: moduleStatusOf(row.reason ?? row.source, row.active),
    price: '0.00',
  }
}

export function overviewFrom(customers: Customer[], pending: unknown, followups: Followup[]): CrmOverview {
  const counts: Record<Stage, number> = { lead: 0, demo: 0, proposal: 0, customer: 0, lost: 0 }
  for (const c of customers) counts[c.stage] += 1
  const active = customers.length - counts.lost
  const today = todayIso()
  const followUps = followups
    .filter((f) => customers.some((c) => c.id === f.customerId))
    .filter((f) => !f.done && !!f.dueOn && daysBetween(f.dueOn, today) <= 10)
    .map((f) => ({
      customerId: f.customerId,
      date: f.dueOn,
      text: f.note,
      kind: (f.channel === 'phone' ? 'call' : 'meeting') as 'call' | 'meeting' | 'collection',
      overdueDays: Math.max(0, daysBetween(today, f.dueOn)),
    }))
    .sort((a, b) => a.date.localeCompare(b.date))
  return {
    counts,
    pendingCollection: money(pending),
    funnel: (['lead', 'demo', 'proposal', 'customer'] as const).map((stage) => ({
      stage,
      count: counts[stage],
      pct: active ? Math.round((counts[stage] / active) * 100) : 0,
    })),
    followUps,
  }
}
